import { type Path as OpentypePath } from 'opentype.js'

export function fmt(n: number): string {
  return parseFloat(n.toFixed(2)).toString()
}

/**
 * Serializes an opentype.js `Path`'s already-positioned commands into an SVG `d` string ourselves,
 * instead of calling `Path.prototype.toPathData()`.
 *
 * Root-caused (WMPCM-9637 follow-up, a live "W" from a variable Times font): `toPathData`'s number
 * formatter (`roundDecimal` in opentype.js) rounds via a string trick —
 * `+(Math.round(decimalPart + "e+" + places) + "e-" + places)` — which silently produces `NaN`
 * whenever `decimalPart` is small enough that `decimalPart.toString()` is ALREADY in exponential
 * notation (roughly `< 1e-6`; e.g. `3.55e-15`, the floating-point residue on `1 + 250 * (72/1000)`
 * for this exact font/scale/coordinate). String-concatenating a second `"e+2"` onto an
 * already-exponential number produces a double-exponent string like `"3.55e-15e+2"`, which
 * `Math.round` cannot parse. Confirmed by hand-reconstructing the real `Glyph`/`Path` objects from
 * the exact raw command data that failed in the wild and reproducing the identical `NaN` via
 * opentype.js's own `toPathData()` — this is a genuine opentype.js bug, not a corrupt font or a bug
 * in this conversion's own math. `n.toFixed()` never emits exponential notation for these
 * magnitudes, so it doesn't hit the bug; per-point rounding also throws away the tiny
 * floating-point residue before it can accumulate the way a global "optimize" pass might.
 */
export function serializeGlyphPath(path: OpentypePath): string {
  const parts: string[] = []
  let sawMoveTo = false

  for (const cmd of path.commands) {
    switch (cmd.type) {
      case 'M':
        // A font glyph contour is always a closed loop — that's what a letterform IS — but at
        // least some fonts' opentype.js output omits the explicit `Z` command even though the
        // contour's last point already coincides with its first (confirmed live: a real glyph
        // came back with one `M` and zero `Z`/`z` commands). Relying on the source's own `Z`
        // silently produced an "open" SVG string for an otherwise perfectly ordinary letter,
        // which made `isShapeTextSupported`'s "is this shape's path closed" check reject the
        // converted shape — blocking the Type tool's "select shape, enter text edit" shortcut for
        // as long as the shape stayed in memory (WMPCM-9637 follow-up). So this closes each
        // contour itself — before a new `M` (multi-contour glyphs, e.g. accented characters) and
        // at the end of the loop below — the same way `pathCommandsToSvgPath` already closes a
        // compound shape's earlier subpaths, rather than trusting the source's own markers.
        if (sawMoveTo) parts.push('Z')
        sawMoveTo = true
        parts.push(`M ${fmt(cmd.x)} ${fmt(cmd.y)}`)
        break
      case 'L':
        parts.push(`L ${fmt(cmd.x)} ${fmt(cmd.y)}`)
        break
      case 'Q':
        parts.push(`Q ${fmt(cmd.x1)} ${fmt(cmd.y1)} ${fmt(cmd.x)} ${fmt(cmd.y)}`)
        break
      case 'C':
        parts.push(
          `C ${fmt(cmd.x1)} ${fmt(cmd.y1)} ${fmt(cmd.x2)} ${fmt(cmd.y2)} ${fmt(cmd.x)} ${fmt(cmd.y)}`
        )
        break
      case 'Z':
        // Skipped rather than pushed — we close every contour ourselves (above, and below for
        // the last one), so trusting this too would either duplicate a `Z` we're already adding
        // or, per the bug this fixes, might never arrive at all.
        break
    }
  }

  if (sawMoveTo) parts.push('Z')

  return parts.join(' ')
}
